import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { downloads, auth } from '../services/auth';
import { useQuery } from 'react-query';

const Dashboard: React.FC = () => {

  const [showReward, setShowReward] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(0);
  const [prevCredits, setPrevCredits] = useState<number | null>(null);

  // ✅ Snackbar state (NEW)
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const navigate = useNavigate();
  const user = auth.getCurrentUser();

  const { data: stats, isLoading } = useQuery(
    'userStats',
    downloads.getStats,
    {
      refetchInterval: 30000,
    }
  );

  const today = new Date();
  const day = today.getDate();
  const month = today.toLocaleString('default', { month: 'short' });
  const todayLabel = `${month} ${day}`;

  const statsCards = [
    {
      title: 'Total Downloads',
      value: stats?.total_downloads || 0,
      icon: '📥',
      color: '#6366f1',
    },
    {
      title: `Today (${todayLabel})`,
      value: stats?.downloads_today || 0,
      icon: '📊',
      color: '#8b5cf6',
    },
    {
      title: 'Remaining',
      value: stats?.premium_status ? '∞' : (stats?.remaining_today || 0),
      icon: '⚡',
      color: stats?.remaining_today > 0 ? '#10b981' : '#ef4444',
    },
    {
      title: 'Status',
      value: stats?.premium_status ? 'Premium' : 'Free',
      icon: stats?.premium_status ? '⭐' : '🔒',
      color: stats?.premium_status ? '#f59e0b' : '#ef4444',
    },
  ];

  useEffect(() => {
    if (!stats) return;

    if (prevCredits !== null) {
      const diff = stats.download_credits - prevCredits;

      if (diff > 0) {
        setShowReward(true);
        setRewardAmount(diff);
        setTimeout(() => setShowReward(false), 8000);
      }
    }

    setPrevCredits(stats.download_credits);
  }, [stats]);

  const glassCard = {
    borderRadius: 3,
    backdropFilter: 'blur(10px)',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.7), rgba(255,255,255,0.4))',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.05)',
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700}>
            Welcome back 👋
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {user?.full_name || user?.email}
          </Typography>
        </Box>

        {/* REWARD */}
        {showReward && (
          <Card sx={{ ...glassCard, mb: 3, borderColor: '#86efac' }}>
            <CardContent>
              <Typography variant="h6" color="success.main">
                🎉 Referral Reward
              </Typography>
              <Typography>
                You earned <strong>+{rewardAmount}</strong> downloads 🚀
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* UPGRADE */}
        {!stats?.premium_status && (
          <Card sx={{ ...glassCard, mb: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">🚀 Go Pro</Typography>
                <Typography color="text.secondary">
                  Unlimited downloads, HD quality, and zero limits.
                </Typography>
                <Button variant="contained" size="large" onClick={() => navigate('/app/premium')}>
                  Upgrade Now
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* STATS */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {statsCards.map((stat) => (
              <Grid item xs={12} sm={6} md={3} key={stat.title}>
                <Card sx={glassCard}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography fontSize={28}>{stat.icon}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ color: stat.color }}
                      >
                        {stat.value}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* WARNINGS */}
        {!stats?.premium_status && stats?.remaining_today <= 1 && stats?.remaining_today > 0 && (
          <Card sx={{ ...glassCard, mb: 3, borderColor: '#fde68a' }}>
            <CardContent>
              <Typography color="warning.main" fontWeight={600}>
                ⚡ Almost out of downloads
              </Typography>
              <Button sx={{ mt: 2 }} variant="contained" color="warning" onClick={() => navigate('/app/premium')}>
                Upgrade
              </Button>
            </CardContent>
          </Card>
        )}

        {!stats?.premium_status && stats?.remaining_today === 0 && (
          <Card sx={{ ...glassCard, mb: 3, borderColor: '#fecaca' }}>
            <CardContent>
              <Typography color="error.main" fontWeight={600}>
                Daily limit reached ⚠️
              </Typography>
              <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/app/premium')}>
                Upgrade Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* PREMIUM */}
        {stats?.premium_status && (
          <Card sx={{ ...glassCard, mb: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography color="success.main" fontWeight={600}>
                  🎉 Premium Active
                </Typography>

                <Chip label="Unlimited Access" color="success" />

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={async () => {
                    const res = await fetch("/api/stripe/billing-portal", {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                      }
                    });

                    const data = await res.json();
                    window.location.href = data.url;
                  }}
                >
                  Manage Subscription
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* BOTTOM SECTION */}
        <Grid container spacing={3}>
          
          {/* ACTIONS */}
          <Grid item xs={12} md={6}>
            <Card sx={glassCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>

                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/app/downloader')}
                  >
                    Process Video
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate('/app/history')}
                  >
                    History
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* REFERRAL */}
          <Grid item xs={12} md={6}>
            <Card sx={glassCard}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Referral</Typography>

                  <Typography variant="body2">
                    🎁 Earn +3 downloads per referral
                  </Typography>

                  <Typography variant="body2">
                    💰 Credits: <strong>{stats?.download_credits || 0}</strong>
                  </Typography>

                  <Divider />

                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#f1f5f9',
                      borderRadius: 2,
                      fontSize: 13,
                      wordBreak: 'break-all',
                    }}
                  >
                    {`${window.location.origin}/register?ref=${user?.referral_code}`}
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}/register?ref=${user?.referral_code}`
                      );
                      setSnackbarOpen(true);
                    }}
                  >
                    Copy Link
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

        </Grid>

        {/* ✅ Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            Copied to clipboard 🚀
          </Alert>
        </Snackbar>

      </Box>
    </Container>
  );
};

export default Dashboard;
